import { first } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { OpsDashboardService } from '../../ops-dashboard.service';
import { Reservation } from '../reservation.model';
import { Table } from 'primeng/table';

@Component({
    selector: 'app-reservation-report',
    templateUrl: './reservation-report.component.html',
    styleUrls: ['./reservation-report.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    // make column heading sticky
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class ReservationReportComponent implements OnInit {
	constructor(
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private opsDashboardService: OpsDashboardService,
		private dialog: MatDialog,
	) {
	}



	// data
	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	reservationReports: any;
	@ViewChild('dt') dt: Table; 
	sortDirection: { [key: string]: boolean } = {};
	private readonly EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
	private readonly EXCEL_EXTENSION = '.xlsx';

	// paging
	first = 0;
	rows = 50;

	// list
	datasource: any;

	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = false;
	exportLoading = false;
	multiSortMeta = [];
	reservations = [];
	defaultStartDate: Date
	defaultEndDate: Date
	channelNames = ['Website', 'Total OTA Aggregate', 'Total OTA', 'Expedia', 'AirBnb', 'VRBO', 'Booking.com', 'Marriott', 'Google'];

	columns: any[] = [
		{ field: 'reservationReportId', header: 'Id', width: 'auto' },
		{ field: 'name', header: 'Name', width: 'auto' },
		{ field: 'description', header: 'Description', width: 'auto' },
		{ field: 'status', header: 'Status', width: 'auto' },
	];

	ngOnInit(): void {
		try {
		// default sort of list
		this.multiSortMeta = [];
		this.multiSortMeta.push({ field: 'createDate', order: 0 });
		this.defaultStartDate = new Date();
		this.defaultStartDate = new Date(this.defaultStartDate.getFullYear(), 0, 1); // jan 1
		this.defaultEndDate = new Date();
		this.defaultEndDate = new Date(this.defaultEndDate.getFullYear(), 12, 0);
		}
		catch (error) {
			console.log('Error: ' + error);
		}
	}

	private getChannelMetric(res: any, channel: string, saleDateRange: string, key: 'numBookings' | 'totalNightlyRateRev'): number {
		const data = res?.channelMap?.[`${channel}-${saleDateRange}`];
		const value = data?.[key];
		return typeof value === 'number' ? value : 0;
	}

	private diffNonNegative(a: number, b: number): number {
		const aNum = typeof a === 'number' ? a : 0;
		const bNum = typeof b === 'number' ? b : 0;
		const diff = aNum - bNum;
		return diff > 0 ? diff : 0;
	}

	private buildReportRow(res: any): any[] {
		const property = res?.propertyCode ?? '';
		const effective = res?.trnsInEffectiveDate ?? '';
		const ttlBookings = typeof res?.numBookings === 'number' ? res.numBookings : 0;
		const ttlNts = typeof res?.numNtsTTL === 'number' ? res.numNtsTTL : 0;
		const avgDta = typeof res?.avgDaysToArrival === 'number' ? res.avgDaysToArrival : 0;
		const avgRate = typeof res?.avgNightlyRate === 'number' ? res.avgNightlyRate : 0;
		const ttlRev = typeof res?.totalNightlyRateRev === 'number' ? res.totalNightlyRateRev : 0;

		const web30Num = this.getChannelMetric(res, 'Website', 'Last 30 Days', 'numBookings');
		const web30Rev = this.getChannelMetric(res, 'Website', 'Last 30 Days', 'totalNightlyRateRev');
		const web60Num = this.getChannelMetric(res, 'Website', 'Last 60 Days', 'numBookings');
		const web60Rev = this.getChannelMetric(res, 'Website', 'Last 60 Days', 'totalNightlyRateRev');
		const web90Num = this.getChannelMetric(res, 'Website', 'Last 90 Days', 'numBookings');
		const web90Rev = this.getChannelMetric(res, 'Website', 'Last 90 Days', 'totalNightlyRateRev');

		// Map OTA30/60/90 to Total OTA Aggregate (1-30, 1-60, 1-90)
		const ota30Num = typeof res?.totalOtaBookingsUpto30Days === 'number' ? res.totalOtaBookingsUpto30Days : 0;
		const ota30Rev = typeof res?.totalOtaRevUpto30Days === 'number' ? res.totalOtaRevUpto30Days : 0;
		const ota60Num = typeof res?.totalOtaBookingsUpto60Days === 'number' ? res.totalOtaBookingsUpto60Days : 0;
		const ota60Rev = typeof res?.totalOtaRevUpto60Days === 'number' ? res.totalOtaRevUpto60Days : 0;
		const ota90Num = typeof res?.totalOtaBookingsUpto90Days === 'number' ? res.totalOtaBookingsUpto90Days : 0;
		const ota90Rev = typeof res?.totalOtaRevUpto90Days === 'number' ? res.totalOtaRevUpto90Days : 0;

		// Map OTA1-30/31-60/61-90 to segmented Total OTA values
		const ota30DaysNum = typeof res?.totalOtaBookings30Days === 'number' ? res.totalOtaBookings30Days : 0;
		const ota30DaysRev = typeof res?.totalOtaRev30Days === 'number' ? res.totalOtaRev30Days : 0;
		const ota60DaysNum = typeof res?.totalOtaBookings60Days === 'number' ? res.totalOtaBookings60Days : 0;
		const ota60DaysRev = typeof res?.totalOtaRev60Days === 'number' ? res.totalOtaRev60Days : 0;
		const ota90DaysNum = typeof res?.totalOtaBookings90Days === 'number' ? res.totalOtaBookings90Days : 0;
		const ota90DaysRev = typeof res?.totalOtaRev90Days === 'number' ? res.totalOtaRev90Days : 0;

		const ota1_30Num = ota30DaysNum;
		const ota1_30Rev = ota30DaysRev;
		const ota31_60Num = this.diffNonNegative(ota60DaysNum, ota30DaysNum);
		const ota31_60Rev = this.diffNonNegative(ota60DaysRev, ota30DaysRev);
		const ota61_90Num = this.diffNonNegative(ota90DaysNum, ota60DaysNum);
		const ota61_90Rev = this.diffNonNegative(ota90DaysRev, ota60DaysRev);

		const channelRange = (channel: string) => {
			const c30Num = this.getChannelMetric(res, channel, 'Last 30 Days', 'numBookings');
			const c30Rev = this.getChannelMetric(res, channel, 'Last 30 Days', 'totalNightlyRateRev');
			const c60NumRaw = this.getChannelMetric(res, channel, 'Last 60 Days', 'numBookings');
			const c60RevRaw = this.getChannelMetric(res, channel, 'Last 60 Days', 'totalNightlyRateRev');
			const c90NumRaw = this.getChannelMetric(res, channel, 'Last 90 Days', 'numBookings');
			const c90RevRaw = this.getChannelMetric(res, channel, 'Last 90 Days', 'totalNightlyRateRev');

			const c31_60Num = this.diffNonNegative(c60NumRaw, c30Num);
			const c31_60Rev = this.diffNonNegative(c60RevRaw, c30Rev);
			const c61_90Num = this.diffNonNegative(c90NumRaw, c60NumRaw);
			const c61_90Rev = this.diffNonNegative(c90RevRaw, c60RevRaw);

			return { c30Num, c30Rev, c31_60Num, c31_60Rev, c61_90Num, c61_90Rev };
		};

		const expedia = channelRange('Expedia');
		const airbnb = channelRange('AirBnb');
		const vrbo = channelRange('VRBO');
		const booking = channelRange('Booking.com');
		const marriott = channelRange('Marriott');
		const google = channelRange('Google');

		return [
			property,
			effective,
			ttlBookings,
			ttlNts,
			avgDta,
			avgRate,
			ttlRev,
			web30Num, web30Rev, web60Num, web60Rev, web90Num, web90Rev,
			ota30Num, ota30Rev, ota60Num, ota60Rev, ota90Num, ota90Rev,
			ota1_30Num, ota1_30Rev, ota31_60Num, ota31_60Rev, ota61_90Num, ota61_90Rev,
			expedia.c30Num, expedia.c30Rev, expedia.c31_60Num, expedia.c31_60Rev, expedia.c61_90Num, expedia.c61_90Rev,
			airbnb.c30Num, airbnb.c30Rev, airbnb.c31_60Num, airbnb.c31_60Rev, airbnb.c61_90Num, airbnb.c61_90Rev,
			vrbo.c30Num, vrbo.c30Rev, vrbo.c31_60Num, vrbo.c31_60Rev, vrbo.c61_90Num, vrbo.c61_90Rev,
			booking.c30Num, booking.c30Rev, booking.c31_60Num, booking.c31_60Rev, booking.c61_90Num, booking.c61_90Rev,
			marriott.c30Num, marriott.c30Rev, marriott.c31_60Num, marriott.c31_60Rev, marriott.c61_90Num, marriott.c61_90Rev,
			google.c30Num, google.c30Rev, google.c31_60Num, google.c31_60Rev, google.c61_90Num, google.c61_90Rev
		];
	}

	exportToExcel(): void {
		this.exportLoading = true;
		setTimeout(() => {
			try {
				const headers = [
					'Property', 'Effective', 'Ttl Bookings', 'Ttl Nts', 'Avg DTA', 'Avg Rate', 'Ttl Rev',
					'Web30 #', 'Web30 $', 'Web60 #', 'Web60 $', 'Web90 #', 'Web90 $',
					'OTA30 #', 'OTA30 $', 'OTA60 #', 'OTA60 $', 'OTA90 #', 'OTA90 $',
					'OTA1-30 #', 'OTA1-30 $', 'OTA31-60 #', 'OTA31-60 $', 'OTA61-90 #', 'OTA61-90 $',
					'Expedia1-30 #', 'Expedia1-30 $', 'Expedia31-60 #', 'Expedia31-60 $', 'Expedia61-90 #', 'Expedia61-90 $',
					'AirBnb1-30 #', 'AirBnb1-30 $', 'AirBnb31-60 #', 'AirBnb31-60 $', 'AirBnb61-90 #', 'AirBnb61-90 $',
					'VRBO1-30 #', 'VRBO1-30 $', 'VRBO31-60 #', 'VRBO31-60 $', 'VRBO61-90 #', 'VRBO61-90 $',
					'Book.Com1-30 #', 'Book.Com1-30 $', 'Book.Com31-60 #', 'Book.Com31-60 $', 'Book.Com61-90 #', 'Book.Com61-90 $',
					'Marriott1-30 #', 'Marriott1-30 $', 'Marriott31-60 #', 'Marriott31-60 $', 'Marriott61-90 #', 'Marriott61-90 $',
					'Google1-30 #', 'Google1-30 $', 'Google31-60 #', 'Google31-60 $', 'Google61-90 #', 'Google61-90 $'
				];

				const aoa: any[] = [headers];
				(this.reservations || []).forEach(res => {
					const row = this.buildReportRow(res);
					aoa.push(row);
				});

				const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoa);
				const workbook: XLSX.WorkBook = { Sheets: { 'Reservations': worksheet }, SheetNames: ['Reservations'] };
				const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
				const data: Blob = new Blob([excelBuffer], { type: this.EXCEL_TYPE });
				const timestamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
				FileSaver.saveAs(data, `reservation-report_${timestamp}${this.EXCEL_EXTENSION}`);
			}
			catch (error) {
				this.messageService.add({ severity: 'error', summary: 'Export failed', detail: (error && error.message) ? error.message : 'Unknown error', life: 3000 });
			}
			finally {
				this.exportLoading = false;
			}
		}, 0);
	}
	
	reloadPage() {
		window.location.reload();
	}

	onSearchSubmit(event: any) {
		this.loading = true;
		const requestObj = {
		  saleDateStart: event.startDate,
		  saleDateEnd: event.endDate,
		};
		this.opsDashboardService.getReservationData(requestObj)
		  .subscribe(
			(res: any) => {
				this.loading = false;
				// this.reservations = res.data.resByPropertyChannelAPI;
				this.reservations = res.data.resByPropertyChannelAPI.map(r => {
					const channelMap = {};

					(r.resByChannelAPI || []).forEach(c => {
						// Use channel + period as key
						channelMap[`${c.refSrcName}-${c.saleDateRange}`] = c;
					});

					return { ...r, channelMap };
				});
				this.reservations.forEach(r => {
					this.channelNames.forEach(channel => {
						if (channel === 'Total OTA') return; // Skip "Total OTA" – it's not from channelMap
						if (channel === 'Total OTA Aggregate') return; // Skip "Total OTA Aggregate" – it's not from channelMap
						['Last 30 Days', 'Last 60 Days', 'Last 90 Days'].forEach(period => {
							const data = r.channelMap[`${channel}-${period}`];
							r[`bookings-${channel}-${period}`] = data ? data.numBookings : 0;
						});
					});
				});

			},
			(err: any) => {
			  this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
			  this.loading = false;
			},
			() => { this.loading = false; });
	
	
	  }

	  getChannelData(res, channel, saleDateRange) {
		return res.channelMap?.[`${channel}-${saleDateRange}`] || null;
	  }

	  getTooltipText(res: any, channel: string, saleDateRange: string): string {
		const data = res.channelMap?.[`${channel}-${saleDateRange}`];
  		if (!data) return 'No data available for this period.';
		
		return `Bookings: ${data.numBookings}
			Avg Days to Arrival: ${data.avgDaysToArrival}d
			Total Nights: ${data.numNtsTTL}d
			Avg Nightly Rate: $${data.avgNightlyRate}
			Total Revenue: $${data.totalNightlyRateRev}`;
	  }

	  // Method for Total OTA and Total OTA Aggregate tooltip
		getTotalOTATooltipText(res: any, channel: string, saleDateRange: string): string {
		if (channel !== 'Total OTA' && channel !== 'Total OTA Aggregate') {
			return ''; // Only process if it's Total OTA or Total OTA Aggregate
		}

		let totalOtaBookingsUptoDays: string;
		let totalOtaAvgDaysToArrivalUptoDays: string;
		let totalOtaNtsTTLUptoDays: string;
		let totalOtaAvgRateUptoDays: string;
		let totalOtaRevUptoDays: string;

		let totalOtaBookingsDays: string;
		let totalOtaAvgDaysToArrivalDays: string;
		let totalOtaNtsTTLDays: string;
		let totalOtaAvgRateDays: string;
		let totalOtaRevDays: string;

		switch (saleDateRange) {
				case 'Last 30 Days':
					totalOtaBookingsUptoDays = res.totalOtaBookingsUpto30Days;
					totalOtaAvgDaysToArrivalUptoDays = res.totalOtaAvgDaysToArrivalUpto30Days;
					totalOtaNtsTTLUptoDays = res.totalOtaNtsTTLUpto30Days;
					totalOtaAvgRateUptoDays = res.totalOtaAvgRateUpto30Days;
					totalOtaRevUptoDays = res.totalOtaRevUpto30Days;

					totalOtaBookingsDays = res.totalOtaBookings30Days;
					totalOtaAvgDaysToArrivalDays = res.totalOtaAvgDaysToArrival30Days;
					totalOtaNtsTTLDays = res.totalOtaNtsTTL30Days;
					totalOtaAvgRateDays = res.totalOtaAvgRate30Days;
					totalOtaRevDays = res.totalOtaRev30Days;
				break;
				case 'Last 60 Days':
					totalOtaBookingsUptoDays = res.totalOtaBookingsUpto60Days;
					totalOtaAvgDaysToArrivalUptoDays = res.totalOtaAvgDaysToArrivalUpto60Days;
					totalOtaNtsTTLUptoDays = res.totalOtaNtsTTLUpto60Days;
					totalOtaAvgRateUptoDays = res.totalOtaAvgRateUpto60Days;
					totalOtaRevUptoDays = res.totalOtaRevUpto60Days;

					totalOtaBookingsDays = res.totalOtaBookings60Days;
					totalOtaAvgDaysToArrivalDays = res.totalOtaAvgDaysToArrival60Days;
					totalOtaNtsTTLDays = res.totalOtaNtsTTL60Days;
					totalOtaAvgRateDays = res.totalOtaAvgRate60Days;
					totalOtaRevDays = res.totalOtaRev60Days;
				break;
				case 'Last 90 Days':
					totalOtaBookingsUptoDays = res.totalOtaBookingsUpto90Days;
					totalOtaAvgDaysToArrivalUptoDays = res.totalOtaAvgDaysToArrivalUpto90Days;
					totalOtaNtsTTLUptoDays = res.totalOtaNtsTTLUpto90Days;
					totalOtaAvgRateUptoDays = res.totalOtaAvgRateUpto90Days;
					totalOtaRevUptoDays = res.totalOtaRevUpto90Days;

					totalOtaBookingsDays = res.totalOtaBookings90Days;
					totalOtaAvgDaysToArrivalDays = res.totalOtaAvgDaysToArrival90Days;
					totalOtaNtsTTLDays = res.totalOtaNtsTTL90Days;
					totalOtaAvgRateDays = res.totalOtaAvgRate90Days;
					totalOtaRevDays = res.totalOtaRev90Days;
				break;
				default:
			return 'No data available for this period.';
		}

		if (channel == 'Total OTA') {
			return `Bookings: ${totalOtaBookingsDays}
				Avg Days to Arrival: ${totalOtaAvgDaysToArrivalDays}d
				Total Nights: ${totalOtaNtsTTLDays}d
				Avg Nightly Rate: $${totalOtaAvgRateDays}
				Total Revenue: $${totalOtaRevDays}`;
		}
		else
		{
			return `Bookings: ${totalOtaBookingsUptoDays}
				Avg Days to Arrival: ${totalOtaAvgDaysToArrivalUptoDays}d
				Total Nights: ${totalOtaNtsTTLUptoDays}d
				Avg Nightly Rate: $${totalOtaAvgRateUptoDays}
				Total Revenue: $${totalOtaRevUptoDays}`;
		}
	}


	  sortDataByDateRange(dateRange: string, channel: string) {
		const sortKey = `${channel}-${dateRange}`;
		this.sortDirection[sortKey] = !this.sortDirection[sortKey];
		const isAscending = this.sortDirection[sortKey];

		this.reservations.sort((a, b) => {
			const aVal = a[`bookings-${channel}-${dateRange}`] || 0;
			const bVal = b[`bookings-${channel}-${dateRange}`] || 0;
			return isAscending ? aVal - bVal : bVal - aVal;
		});
	}
}
